import { gql } from '@apollo/client';
import {
  CHAPTERS_DTO,
  PLAY_CHAPTERS_FRAGMENT,
  CHAPTER_PURCHASES_DTO,
  CURRENT_PLAYING_CHAPTER,
  POPUP_PROFILE_IMAGE_DTO,
  CHARACTER_DTO,
} from '@api/fragment/chapters';

export const HOME_STORY_DTO = gql`
  fragment HomeStoryDto on Story {
    type
    storyId
    name
    authorName
    onHiatus
    isOriginal
    isUp
    isTimeLeapFree
    isFinished
    introImageFile {
      link
    }
    mainImageFile {
      link
    }
    teaserVideoFile {
      link
    }
    genreKey {
      text {
        key
      }
    }
    shortDesc
    sectionType
    topic
    language {
      display
      code
    }
  }
`;

export const STORY_DTO = gql`
  fragment StoryDto on Story {
    storyId
    name
    type
    coverText
    author {
      name
    }
    authorName
    introImageFile {
      link
    }
    mainImageFile {
      link
    }
    previewImageFile {
      link
    }
    sectionType
    topic
    targetAge
    publishedAt
    storyEndsAt
    coinForRestart
    oneLineDesc
    isFinished
    shortDesc
    representedAt
    isDummy
    lastPlay {
      currentPlayingChapter {
        selectedChoices {
          updatedAt
        }
      }
    }
    teaserVideoFile {
      link
    }
    wideImageFile {
      link
    }
    ipSourcing {
      iconImageFile {
        link
      }
    }
    isMWS
    lastPlay {
      userId
      firstName
      lastName
    }
    endingInfo {
      endings {
        ending {
          name
          endingId
          storyId
          imageFile {
            link
          }
        }
        userHasEnding {
          ending {
            endingId
          }
        }
      }
    }
  }
`;

export const BUNDLE_FEATURED_DTO = gql`
  fragment BundleFeaturedDto on Featured {
    bundleFeaturedId
    story {
      name
    }
    storyId
    coverImageFile {
      link
    }
    link
    language {
      display
      code
    }
    countryCode
  }
`;

export const ENDING_DTO = gql`
  fragment EndingDto on Ending {
    endingId
    name
    collectionDescription
    previewName
    isFinal
    rateLevel
    arrivalRate
    shareKey
    displayName
    story {
      name
    }
    imageFile {
      link
    }
    previewImageFile {
      link
    }
    additionalCards {
      endingId
      type
      character {
        imageFile {
          link
        }
      }
      choiceCount
      arrivalRate
      endingCollectRate
    }
  }
`;

/**
 * 획득한 엔딩인 경우에 사용하는 데이터
 */
export const USER_HAS_ENDING_DTO = gql`
  fragment UserHasEndingDto on UserHasEnding {
    endingId
    customDescription
    customName
    updatedAt
    uheId
    hasNewPlayerReport
    hasPlayerReport
  }
`;

export const ENDINGS_INFO_DTO = gql`
  fragment EndingsInfoDto on Story {
    endingInfo {
      numTotalEnding
      endings {
        userHasEnding {
          ...UserHasEndingDto
        }
        ending {
          ...EndingDto
        }
      }
    }
  }
  ${USER_HAS_ENDING_DTO}
  ${ENDING_DTO}
`;

export const PREVIEW_ENDINGS_DTO = gql`
  fragment PreviewEndingsDto on Story {
    previewEndingInfo {
      previewEndings {
        name
        isHidden
        imageFile {
          link
        }
        rateLevel
      }
      numTotalPreviewEnding
    }
  }
`;

export const PROPERTIES_DTO = gql`
  fragment PropertiesDto on Story {
    properties {
      propId
      propName
      propNameRaw
      maxValue
      imageFile {
        link
      }
      isMain
    }
  }
`;

export const LAST_PLAY_DTO = gql`
  fragment LastPlayDto on Story {
    lastPlay {
      userId
      firstName
      lastName
      currentPlayingChapter {
        chapterId
        chapter {
          chapterIndex
        }
        ending {
          name
          displayName
          endingId
          isFinal
          imageFile {
            link
          }
        }
        playingCharacters {
          characterId
          firstName
          lastName
          profileImageFileId
          character {
            name
          }
        }
      }
      playedChapters {
        chapterId
        playingStoryId
        chapter {
          chapterIndex
        }
        ending {
          name
          displayName
          endingId
          isFinal
          rateLevel
          arrivalRate
        }
      }
    }
  }
`;

export const STORY_META = gql`
  fragment StoryMetaDto on Story {
    name
    storyId
    defaultFirstName
    defaultLastName
    mainCharacterImageFile {
      fileId
      link
    }
    characters {
      name
      imageFile {
        link
      }
    }
    backgrounds {
      name
      file {
        link
      }
    }
    chapters {
      chapterId
      chapterIndex
      name
    }
    isSubscribed
    hasAchievement
    hasActiveStoryItem
    userItems

    ...EndingsInfoDto
    ...ChapterPurchasesDto
    ...PropertiesDto
    ...LastPlayDto
  }
  ${ENDINGS_INFO_DTO}
  ${CHAPTER_PURCHASES_DTO}
  ${PROPERTIES_DTO}
  ${LAST_PLAY_DTO}
`;

export const ON_BOARDING_POPUP_CHOICE = gql`
  fragment OnboardingPopupChoice on OnboardingPopupChoice {
    createdAt
    updatedAt
    popupChoiceId
    popupId
    imageFile {
      link
    }
    imageFileId
    content
    showOrder
    choiceProps
    popup {
      popupId
      storyId
    }
    status
    playerClassId
    playerClass {
      name
    }
  }
`;

export const ON_BOARDING_POPUP = gql`
  fragment OnBoardingPopup on OnboardingPopup {
    createdAt
    updatedAt
    popupId
    storyId
    chapterId
    description
    onlyName
    type
    showOrder
    price
    story {
      ...StoryMetaDto
    }
    chapter {
      ...ChaptersDto
    }
    popupChoices {
      ...OnboardingPopupChoice
    }
    visibleConditions
  }
  ${STORY_META}
  ${CHAPTERS_DTO}
  ${ON_BOARDING_POPUP_CHOICE}
`;

export const PLAY_INFO_DTO = gql`
  fragment PlayInfoDto on UserPlayedStory {
    firstName
    lastName
    storyId
    story {
      name
      shareKey
      chapters {
        ...ChaptersDto
      }
      isSubscribed
      ...PropertiesDto
    }
    playedChapters {
      ...PlayChapterDto
    }
    currentPlayingChapter {
      ...PlayChapterDto
    }
  }
  ${CHAPTERS_DTO}
  ${PROPERTIES_DTO}
  ${PLAY_CHAPTERS_FRAGMENT}
  ${PLAY_CHAPTERS_FRAGMENT}
`;

export const REACHED_ENDING_DTO = gql`
  fragment ReachedEndingDto on Ending {
    ...EndingDto
    story {
      isFinished
      storyId
    }
    chapter {
      endings {
        endingId
      }
    }
  }
  ${ENDING_DTO}
`;

export const BADGE_DTO = gql`
  fragment BadgeDto on ABadgeWithAcquisition {
    badge {
      aBadgeId
      description
      display
      displayLocked
      acquired
      imageLink
      imageLockedLink
      name
    }
    progress {
      current
      expected
    }
  }
`;

export const ITEM_DTO = gql`
  fragment ItemDto on StoryItem {
    storyItemId
    name
    displayName
    desc
    imageFile {
      link
    }
    isHidden
  }
`;

export const STORY_DETAIL_SEO = gql`
  fragment StoryDetailSEO on Story {
    name
    introImageFile {
      link
    }
    storygameCoverImageFile {
      link
    }
    playType
    shortDesc
    genreKey {
      text {
        key
      }
    }
  }
`;

export const STORY_DETAIL_HEADER = gql`
  fragment StoryDetailHeader on Story {
    name
    storyId
    teaserVideoFile {
      link
    }
    introImageFile {
      link
    }
    mainImageFile {
      link
    }
    isWaiting
    view
    isFinished
    isUp
    onHiatus
    storyWeekdays {
      weekday
    }
    playType
    language {
      code
      display
    }
    genreKey {
      type
      text {
        key
      }
    }
    bundle {
      storyBundleId
    }
    isAdult
  }
`;

export const STORY_DETAIL_SUMMARY = gql`
  fragment StoryDetailSummary on Story {
    storyId
    authorName
    name
    author {
      name
    }
    isFinished
    topic
    targetAge
    oneLineDesc
    shareKey
    totalNumOfChapterComments
    sectionType
    price
    myFavorite
    storyWeekdays {
      weekday
    }
    onHiatus
    isAdult
    chapters {
      chapterId
    }
    storygame {
      storyId
      name
      storygameCoverImageFile {
        link
      }
      isStorygameHidden
    }
    storyPriceSetting {
      timeLeapFreePerChapterSetting
    }
  }
`;

export const STORY_DETAIL_BUTTON = gql`
  fragment StoryDetailButton on Story {
    storyId
    name
    purchasable
    purchased
    lastPreviewChapterIndex
    originalPrice
    price
    discountRate
    mainImageFile {
      link
    }
    introImageFile {
      link
    }
    isWaiting
    chapters {
      ...ChaptersDto
    }
    language {
      code
      display
    }
    bundleId
    languageCode
    genreKey {
      type
    }
    enabledPurchasedChapter {
      chapterCount
      coinAmount
      hasAdultChapter
    }
    playType
    ...ChapterPurchasesDto
    ...LastPlayDto
  }
  ${LAST_PLAY_DTO}
  ${CHAPTER_PURCHASES_DTO}
  ${CHAPTERS_DTO}
`;

export const STORY_DETAIL_POINTS = gql`
  fragment StoryDetailPoints on Story {
    name
    storyId
    language {
      code
      display
    }
    ...PropertiesDto
  }
  ${PROPERTIES_DTO}
`;

export const STORY_DETAIL_ENDING_LIST = gql`
  fragment StoryDetailEndingList on Story {
    isFinished
    defaultFirstName
    defaultLastName
    lastPlay {
      userId
      firstName
      lastName
    }
    wideImageFile {
      link
    }
    mainImageFile {
      link
    }
    ...EndingsInfoDto
    ...PreviewEndingsDto
  }
  ${ENDINGS_INFO_DTO}
  ${PREVIEW_ENDINGS_DTO}
`;

export const STORY_DETAIL_CHAPTER_LIST = gql`
  fragment StoryDetailChapterList on Story {
    name
    storyId
    coinForRestart
    purchasable
    purchased
    lastPreviewChapterIndex
    originalPrice
    price
    discountRate
    bundleId
    languageCode
    genreKey {
      type
    }
    playType
    mainImageFile {
      link
    }
    introImageFile {
      link
    }
    chapters {
      ...ChaptersDto
    }
    ...ChapterPurchasesDto
    ...LastPlayDto
  }
  ${CHAPTERS_DTO}
  ${LAST_PLAY_DTO}
  ${CHAPTER_PURCHASES_DTO}
`;

export const STORY_DETAIL_MY_ENDING = gql`
  fragment StoryDetailMyEnding on Story {
    isWaiting
    ...LastPlayDto
  }
  ${LAST_PLAY_DTO}
`;

export const STORY_CHARACTERS = gql`
  fragment StoryCharacters on Story {
    characters {
      chrId
      name
      defaultFirstName
      defaultLastName
      displayName
    }
  }
`;

/** 채팅 온보딩팝업 정보 */
export const CHAT_ONBOARDING_POPUPS = gql`
  fragment ChatOnboardingPopups on OnboardingPopup {
    paymentProduct {
      amount
      inappCoinId
      inappProductId
      price
      originalPrice
      originalPriceFloat
      priceFloat
    }
    popupId
    description
    onlyName
    type
    status
    showOrder
    visibleConditions
    price
    maxAdCount
    character {
      ...CharacterDto
    }
    popupChoices {
      ...OnboardingPopupChoice
    }
    popupProfileImages {
      ...PopupProfileImageDto
    }
    canUploadProfileImage
  }

  ${CHARACTER_DTO}
  ${ON_BOARDING_POPUP_CHOICE}
  ${POPUP_PROFILE_IMAGE_DTO}
`;

export const LAST_PLAYED_DTO = gql`
  fragment LastPlayedDto on UserPlayedStory {
    userId
    firstName
    lastName
    currentPlayingChapter {
      ...CurrentPlayingChapter
    }
    playedChapters {
      chapterId
      playingStoryId
      ending {
        name
        endingId
        chapterId
        updatedAt
        isFinal
      }
      currentItems
      chapter {
        chapterIndex
      }
    }
  }
  ${CURRENT_PLAYING_CHAPTER}
`;
// cover image 추가
export const CHAT_STORY_DTO = gql`
  fragment ChatStoryDto on Story {
    storyId
    discoveredUserProps
    coinProduct {
      inappProductId
      inappCoinId
      productName
      amount
      originalPrice
      price
    }
    mainCharacterPortraitFile {
      fileId
      link
    }
    characters {
      portraitFile {
        fileId
        link
      }
    }
    endingInfo {
      numTotalEnding
      endings {
        userHasEnding {
          ...UserHasEndingDto
        }
        ending {
          ...EndingDto
        }
      }
    }
    storyItems {
      desc
      displayName
      imageFile {
        link
      }
      isHidden
      storyItemId
      name
    }
    bundle {
      genre
      storyBundleId
    }
    language {
      code
    }
    genreKey {
      text {
        key
      }
      title {
        key
      }
      subTitle {
        key
      }
      type
    }
    playType
    name
    defaultFirstName
    defaultLastName
    isFixMainChrName
    sectionType
    purchasable
    purchased
    lastPreviewChapterIndex
    isAdult
    storygameCoverImageFile {
      link
    }
    mainCharacterImageFile {
      fileId
      link
    }
    bannerImageFile {
      link
    }
    wideImageFile {
      link
    }
    characters {
      chrId
      name
      defaultFirstName
      defaultLastName
      displayName
      imageFile {
        fileId
        link
      }
      imageFileId
    }
    backgrounds {
      name
      file {
        link
      }
      storygameBackgrounds {
        shfId
        platformType
        fileId
        name
        file {
          link
        }
      }
    }
    chapters {
      chapterId
      chapterIndex
      name
      story {
        lastPlay {
          currentPlayingChapter {
            currentItems
          }
        }
      }
      price
      hasNewBadge
      hasGraphIcon
      hasBGM
      freedAt
      freeDependencyChapterIndex
      isTimeLeapFree
      endings {
        endingId
      }
    }
    lastPlay {
      ...LastPlayedDto
    }
    properties {
      propId
      propName
      propNameRaw
      maxValue
      imageFile {
        link
      }
      isMain
      isPrimary
      displayPropName
      isUsed
      propType
      showOrder
      unit
    }
    price
    originalPrice
    discountRate
    coinForRestart
    mainImageFile {
      link
    }
    introImageFile {
      link
    }
    hasAdultChapter
    storyPriceSetting {
      timeLeapFreePerChapterSetting
    }
  }
  ${LAST_PLAYED_DTO}
  ${USER_HAS_ENDING_DTO}
  ${ENDING_DTO}
`;

export const COMMENT_DTO = gql`
  fragment CommentDto on ChapterComment {
    createdAt
    updatedAt
    ccId
    story {
      storyId
      name
    }
    chapter {
      name
      chapterIndex
      chapterId
    }
    author {
      name
      userId
    }
    authorId
    isHidden
    isSpoiler
    content
    numOfLikes
    contentEditedAt
    deletedAt
    likedByMe
  }
`;

export const REPLY_DTO = gql`
  fragment ReplyDto on ChapterComment {
    ...CommentDto
    parent {
      ...CommentDto
    }
  }
  ${COMMENT_DTO}
`;

export const REPORTING_REASON_DTO = gql`
  fragment ReportingReasonDto on ChapterCommentReportingReason {
    reasonId
    showOrder
    title
    description
    hasUnderSeparator
    hasCustomReason
    isRealtimeUpdate
  }
`;
